import {
  DynamicListOption,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import { abbreviateDate } from '@utils/date';

import { TravellerJourneyActionDetailed } from 'api/types';

// A dynamic-list option is an object carrying both a value and a label (e.g. { label, value,
// shortId }). Both keys are required so file objects ({ src, name }) and other structured values
// are not mistaken for one.
export const isDynamicListOption = (value: unknown): value is Partial<DynamicListOption> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'value' in value &&
  'label' in value;

const optionText = (option: Partial<DynamicListOption>) => option.label ?? option.value ?? '';

const resolveRequestDataText = (
  action: TravellerJourneyActionDetailed<'integration'>,
  field: IntegrationEndpointVariable
) => {
  const value = action.data.requestData[field.name];
  if (field.type === 'date' && value) {
    return abbreviateDate(value, 'en');
  }
  if (field.type === 'select') {
    if (isDynamicListOption(value)) {
      return optionText(value);
    }
    const selectedOption = field.options?.find((option) => value === option.value);
    return selectedOption?.label ?? value;
  }
  if (field.type === 'array_of_strings' && Array.isArray(value) && field.options?.length) {
    return value
      .map((item) => field.options?.find((option) => option.value === item)?.label ?? item)
      .join('\n');
  }
  if (field.type === 'boolean') {
    return String(value ?? false);
  }
  return value;
};

export const getRequestDataText = (
  action: TravellerJourneyActionDetailed<'integration'>,
  field: IntegrationEndpointVariable
) => {
  const text = resolveRequestDataText(action, field);
  // Never hand React a bare object/array as a child. Non-select fields should resolve to a plain
  // value once the backend stops enriching them; until then — and for any unexpected shape — this
  // degrades to text instead of crashing the sidebar.
  return typeof text === 'object' && text !== null ? JSON.stringify(text) : text;
};
